import React, { useState, useEffect } from 'react';
import NumberFlow from '@number-flow/react';

const JPY_TO_TRY_RATE = 0.28; // 1 JPY = ₺0.28

interface StreamOverlayProps {
    /** Para miktarı (TRY) */
    amountTRY: number;
    /** Önceki miktar (TRY) */
    previousAmountTRY?: number;
    /** Overlay'in görünür olup olmadığı */
    isVisible: boolean;
    /** Animasyon tamamlandığında çağrılacak fonksiyon */
    onAnimationComplete?: () => void;
    /** Overlay başlığı */
    title?: string;
    /** Artış/azalışı göster */
    showChange?: boolean;
    /** Stats - Son 7 gün */
    last7Days?: number;
    /** Stats - Son 30 gün */
    last30Days?: number;
    /** Stats - Bugün */
    todaySpending?: number;
}

export const StreamOverlay: React.FC<StreamOverlayProps> = ({
    amountTRY,
    previousAmountTRY = 0,
    isVisible,
    onAnimationComplete,
    title = 'Toplam Harcama',
    showChange = true,
    last7Days = 0,
    last30Days = 0,
    todaySpending = 0,
}) => {
    const [animationClass, setAnimationClass] = useState('');

    const change = amountTRY - previousAmountTRY;
    const isIncrease = change > 0;
    const amountJPY = Math.round(amountTRY / JPY_TO_TRY_RATE);

    useEffect(() => {
        if (isVisible) {
            setAnimationClass('animate-slide-in-up');
        } else {
            setAnimationClass('animate-slide-out-down');
            const timer = setTimeout(() => {
                onAnimationComplete?.();
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isVisible, onAnimationComplete]);

    return (
        <div className="fixed bottom-6 right-6 z-50">
            <div
                className={`
          relative
          w-[500px] aspect-[1024/836]
          transition-all duration-300
          ${isVisible ? 'animate-slide-in-up opacity-100' : 'animate-slide-out-down opacity-0 pointer-events-none'}
        `}
                style={{
                    backgroundImage: "url('/harcama_bg.webp')",
                    backgroundSize: '100% 100%',
                    backgroundRepeat: 'no-repeat',
                    transform: isVisible ? 'translateY(0)' : 'translateY(100%)'
                }}
            >
                {/* Ana Para Miktarı (TRY ve JPY) - Toplam Harcama yazısının altında */}
                <div className="absolute top-[40%] left-0 w-full flex flex-col items-center justify-center gap-0 transform -translate-y-1/2">
                    <div className="flex items-center gap-2">
                        <span className="text-amber-400 text-3xl font-bold font-serif">₺</span>
                        <NumberFlow
                            value={amountTRY}
                            format={{ minimumFractionDigits: 0, maximumFractionDigits: 0 }}
                            transformTiming={{ duration: 800, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
                            spinTiming={{ duration: 600, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
                            className="text-white text-6xl font-bold font-serif tracking-wider drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]"
                        />
                    </div>
                    <div className="flex items-center gap-1 opacity-80">
                        <span className="text-yellow-500/80 text-2xl font-bold font-serif">¥</span>
                        <NumberFlow
                            value={amountJPY}
                            format={{ minimumFractionDigits: 0, maximumFractionDigits: 0 }}
                            transformTiming={{ duration: 800, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
                            spinTiming={{ duration: 600, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
                            className="text-yellow-500/80 text-4xl font-bold font-serif drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]"
                        />
                    </div>
                    {/* Değişim Göstergesi */}
                    {showChange && change !== 0 && (
                        <div
                            className={`
                                flex items-center gap-1 px-3 py-1 mt-2 rounded-full text-lg font-bold shadow-lg
                                ${isIncrease
                                    ? 'bg-emerald-900/60 text-emerald-400 border border-emerald-500/50'
                                    : 'bg-red-900/60 text-red-400 border border-red-500/50'
                                }
                            `}
                        >
                            <span className="text-xs">{isIncrease ? '▲' : '▼'}</span>
                            <span>₺{Math.abs(change).toFixed(0)}</span>
                        </div>
                    )}
                </div>

                {/* Alt değerler - görsel içindeki 3 sütunla hizalı tek satır */}
                <div className="absolute bottom-[12%] left-0 w-full flex items-center" style={{ paddingLeft: '7%', paddingRight: '7%' }}>
                    {/* Bugün */}
                    <div className="flex-1 flex justify-center">
                        <div className="text-amber-100 text-xl font-bold font-serif flex items-center"
                             style={{ textShadow: '0 0 12px rgba(255,200,100,0.8), 0 2px 8px rgba(0,0,0,1), 1px 1px 0 rgba(0,0,0,1), -1px -1px 0 rgba(0,0,0,1)' }}>
                            <span className="mr-0.5 text-sm text-amber-400">₺</span>
                            <NumberFlow
                                value={todaySpending}
                                format={{ minimumFractionDigits: 0, maximumFractionDigits: 0 }}
                                transformTiming={{ duration: 600, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
                                spinTiming={{ duration: 400, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
                            />
                        </div>
                    </div>
                    {/* Bu Hafta */}
                    <div className="flex-1 flex justify-center">
                        <div className="text-amber-100 text-xl font-bold font-serif flex items-center"
                             style={{ textShadow: '0 0 12px rgba(255,200,100,0.8), 0 2px 8px rgba(0,0,0,1), 1px 1px 0 rgba(0,0,0,1), -1px -1px 0 rgba(0,0,0,1)' }}>
                            <span className="mr-0.5 text-sm text-amber-400">₺</span>
                            <NumberFlow
                                value={last7Days}
                                format={{ minimumFractionDigits: 0, maximumFractionDigits: 0 }}
                                transformTiming={{ duration: 600, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
                                spinTiming={{ duration: 400, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
                            />
                        </div>
                    </div>
                    {/* Bu Ay */}
                    <div className="flex-1 flex justify-center">
                        <div className="text-amber-100 text-xl font-bold font-serif flex items-center"
                             style={{ textShadow: '0 0 12px rgba(255,200,100,0.8), 0 2px 8px rgba(0,0,0,1), 1px 1px 0 rgba(0,0,0,1), -1px -1px 0 rgba(0,0,0,1)' }}>
                            <span className="mr-0.5 text-sm text-amber-400">₺</span>
                            <NumberFlow
                                value={last30Days}
                                format={{ minimumFractionDigits: 0, maximumFractionDigits: 0 }}
                                transformTiming={{ duration: 600, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
                                spinTiming={{ duration: 400, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
                            />
                        </div>
                    </div>
                </div>

                {/* Pulse efekti (sadece artış olduğunda) */}
            </div>
        </div>
    );
};
